import { database, storage } from './firebaseConfig';
import { ref as dbRef, get, remove, set } from "firebase/database";
import { ref as storageRef, getDownloadURL, deleteObject } from "firebase/storage";
import { hasPermission, Permissions } from './services/authService';

export const fetchAlbums = async () => {
  const albumsRef = dbRef(database, 'gallery/legacy/albums');
  const snapshot = await get(albumsRef);
  const albums = snapshot.val();
  // Assuming each album has a 'date' field for sorting purposes
  return albums;
};

export const fetchPhotos = async () => {
  const photosRef = dbRef(database, 'gallery/legacy/photos');
  const snapshot = await get(photosRef);
  return snapshot.val();
};

export const getPhotoUrl = async (path) => {
  const formattedPath = path.replace('gs://baue-gallery.appspot.com/', '');
  const storageReference = storageRef(storage, formattedPath);
  try {
    const url = await getDownloadURL(storageReference);
    return url;
  } catch (error) {
    console.error("Error getting download URL", error);
    return null;
  }
};

export const getCoverImageUrl = async (coverImage) => {
  if (!coverImage) {
    console.warn("No cover image provided.");
    return 'default_cover.jpg'; 
  }

  if (coverImage.startsWith("http")) {
    console.log("Using direct URL for cover image:", coverImage);
    return coverImage;
  } else {
    try {
      console.log("Attempting to fetch legacy photo URL by ID:", coverImage);
      const photoRef = dbRef(database, `gallery/legacy/photos/${coverImage}`);
      const snapshot = await get(photoRef);

      if (snapshot.exists()) {
        const photoData = snapshot.val();
        if (photoData.url) {
          if (photoData.url.startsWith("gs://")) {
            const storageRefPath = photoData.url.replace("gs://baue-gallery.appspot.com/", "");
            const storageRefObj = storageRef(storage, storageRefPath);
            const httpUrl = await getDownloadURL(storageRefObj);
            console.log("Converted gs:// URL to HTTP:", httpUrl);
            return httpUrl;
          }
          console.log("Legacy album cover URL found:", photoData.url);
          return photoData.url;
        } else {
          console.warn(`Legacy album cover image with ID ${coverImage} does not contain a URL.`);
        }
      } else {
        console.warn(`Photo with ID ${coverImage} does not exist in the database.`);
      }
    } catch (error) {
      console.error("Error fetching legacy photo URL:", error);
    }

    // Fallback to default if ID lookup fails
    return 'default_cover.jpg';
  }
};

export const deleteAlbum = async (albumId, userRole, userId) => {
  console.log('Deleting album:', albumId, 'by user:', userId, 'with role:', userRole);
  
  console.log('Current user role:', userRole);
  console.log('Required permission:', Permissions.DELETE_ALBUM);
  console.log('Has permission result:', hasPermission(userRole, Permissions.DELETE_ALBUM));

  if (!hasPermission(userRole, Permissions.DELETE_ALBUM)) {
    console.error('Permission denied: user lacks DELETE_ALBUM permission');
    console.error('User role:', userRole);
    console.error('Required permission:', Permissions.DELETE_ALBUM);
    throw new Error('Unauthorized to delete albums');
  }

  const albumRef = dbRef(database, `gallery/legacy/albums/${albumId}`);
  const albumSnapshot = await get(albumRef);
  const album = albumSnapshot.val();
  
  console.log('Album data:', album);

  if (userRole !== 'admin' && album.legacy_owner_id !== userId) {
    console.error('Permission denied: user is not admin or owner');
    throw new Error('Unauthorized to delete this album');
  }

  try {
    await remove(albumRef);
    console.log('Album successfully deleted');
    return true;
  } catch (error) {
    console.error('Error deleting album:', error);
    throw error;
  }
};

export const deletePhoto = async (photoId, userRole, userId) => {
  if (!hasPermission(userRole, Permissions.DELETE_PHOTO)) {
    throw new Error('Unauthorized to delete photos');
  }

  const photoRef = dbRef(database, `gallery/legacy/photos/${photoId}`);
  const photoSnapshot = await get(photoRef);
  const photo = photoSnapshot.val();

  // Only admin or owner can delete
  if (userRole !== 'admin' && photo.legacy_owner_id !== userId) {
    throw new Error('Unauthorized to delete this photo');
  }

  // Delete from storage
  const storageReference = storageRef(storage, photo.url);
  await deleteObject(storageReference);

  // Delete from database
  await remove(photoRef);
};

export const updateAlbum = async (albumId, updates, userRole, userId) => {
  if (!hasPermission(userRole, Permissions.EDIT_ALBUM)) {
    throw new Error('Unauthorized to edit albums');
  }

  const albumRef = dbRef(database, `gallery/legacy/albums/${albumId}`);
  const albumSnapshot = await get(albumRef);
  const album = albumSnapshot.val();

  // Only admin or owner can edit
  if (userRole !== 'admin' && album.legacy_owner_id !== userId) {
    throw new Error('Unauthorized to edit this album');
  }

  await set(albumRef, { ...album, ...updates });
};

export const updatePhoto = async (photoId, updates, userRole, userId) => {
  if (!hasPermission(userRole, Permissions.EDIT_PHOTO)) {
    throw new Error('Unauthorized to edit photos');
  }

  const photoRef = dbRef(database, `gallery/legacy/photos/${photoId}`);
  const photoSnapshot = await get(photoRef);
  const photo = photoSnapshot.val();

  // Only admin or owner can edit
  if (userRole !== 'admin' && photo.legacy_owner_id !== userId) {
    throw new Error('Unauthorized to edit this photo');
  }

  await set(photoRef, { ...photo, ...updates });
};